import * as THREE from 'three';
import GameObject from 'scripts/engine/game-object';
import Planet from 'scripts/objects/geo-planet';
import Background from 'scripts/objects/background';
import Axes from 'scripts/objects/axes';
import { UpdateState } from 'scripts/engine/engine';
import { Model } from 'scripts/model-loader';
import TargetCamera from 'scripts/cameras/target-camera';
import { CallbackSet } from 'scripts/engine/helpers';
import ChangeHistory from 'scripts/change-history';

export interface EditorTool {
    name: string,
    icon: string,
    brushChannel: BrushChannel,
    description: string,
    isDrawingTool: boolean
}

export enum BrushChannel {
    None, Terrain, Scenery
}

export const EditorTools = {
    raise: {
        name: 'Raise',
        icon: 'raise',
        brushChannel: BrushChannel.Terrain,
        description: 'Raise terrain',
        isDrawingTool: true
    },
    lower: {
        name: 'Lower',
        icon: 'lower',
        brushChannel: BrushChannel.Terrain,
        description: 'Lower terrain',
        isDrawingTool: true
    },
    smooth: {
        name: 'Smooth',
        icon: 'smooth',
        brushChannel: BrushChannel.Terrain,
        description: 'Smooth terrain',
        isDrawingTool: true
    },
    level: {
        name: 'Level',
        icon: 'level',
        brushChannel: BrushChannel.Terrain,
        description: 'Level terrain',
        isDrawingTool: true
    },
    paint: {
        name: 'Paint',
        icon: 'brush',
        brushChannel: BrushChannel.Terrain,
        description: 'Paint terrain',
        isDrawingTool: true
    },
    items: {
        name: 'Items',
        icon: 'tree',
        brushChannel: BrushChannel.Scenery,
        description: 'Add scenery',
        isDrawingTool: true
    },
    clear: {
        name: 'Clear',
        icon: 'trash',
        brushChannel: BrushChannel.Scenery,
        description: 'Clear scenery',
        isDrawingTool: true
    },
    water: {
        name: 'Water',
        icon: 'water',
        brushChannel: BrushChannel.None,
        description: 'Water settings',
        isDrawingTool: false
    },
    atmosphere: {
        name: 'Atmosphere',
        icon: 'cloud',
        brushChannel: BrushChannel.None,
        description: 'Atmosphere settings',
        isDrawingTool: false
    },
    settings: {
        name: 'Misc.',
        icon: 'gear',
        brushChannel: BrushChannel.None,
        description: 'Misc.',
        isDrawingTool: false
    }
}

class PlanetEditorScene extends GameObject {
    axes: Axes;
    planet: Planet;
    background: Background;
    tool: EditorTool | undefined;
    color: number;
    model: Model | undefined;
    flattenHeight: number | undefined;
    cameraController: TargetCamera;
    onUpdateCallback: CallbackSet;
    onBrushSizeUpdateCallback: CallbackSet;
    isDrawing: boolean;
    history: ChangeHistory;
    brushSizeDisplay: number;
    _brushSize: number;
    _saveStateTimeout: any;

    constructor() {
        super();
        this.axes = new Axes(100);
        this.axes.visible = false;
        this.cameraController = new TargetCamera();
        this.engine.camera.position.set(10, 5, 10);
        this.engine.camera.lookAt(new THREE.Vector3(0, 0, 0));
        this.cameraController.transitionToTarget(new THREE.Vector3(), 1.0, 5.0);
        this.planet = new Planet();
        this.cameraController.addHeightLimiter((position: THREE.Vector3, direction: THREE.Vector3) =>
            this.planet.terrain.getHeightAtPosition(position, direction)
        );
        this.cameraController.addHeightLimiter(() =>
            this.planet.water.displayHeight
        );
        this.planet.terrain.onBeforeChange.addListener(this, () => {
            this.setSaveStateTimeout();
        });
        this.planet.scenery.onBeforeChange.addListener(this, () => {
            this.setSaveStateTimeout();
        });
        this.planet.colorPalette.onBeforeChange.addListener(this, () => {
            this.setSaveStateTimeout();
        });
        this.planet.atmosphere.onBeforeChange.addListener(this, () => {
            this.setSaveStateTimeout();
        });
        this.planet.water.onBeforeChange.addListener(this, () => {
            this.setSaveStateTimeout();
        });
        this.background = new Background(500, 20, 100);
        this.history = new ChangeHistory();
        this.color = 0;
        this.onUpdateCallback = new CallbackSet();
        this.onBrushSizeUpdateCallback = new CallbackSet();
        this.isDrawing = false;
        this.brushSizeDisplay = 1.0;
        this._brushSize = 1.0;
        this._saveStateTimeout = undefined;
    }

    loadData(data: string, clearHistory = false): boolean {
        const currentPlanetData = this.planet.serialize();
        const success = this.planet.deserialize(data);
        if (clearHistory) {
            this.history.clearHistory();
        } else {
            this.history.addUndo(currentPlanetData);
            this.history.clearRedos();
            this.saveToLocalStorage(currentPlanetData);
        }
        return success;
    }

    setSaveStateTimeout() {
        const reset = () => {
            this._saveStateTimeout = undefined;
            this.saveToLocalStorage();
        };
        if (this._saveStateTimeout === undefined) {
            this.history.addUndo(this.planet.serialize());
            this.history.clearRedos();
        } else {
            clearTimeout(this._saveStateTimeout);
        }
        this._saveStateTimeout = setTimeout(reset, 500);
    }

    saveToLocalStorage(data: string | null = null) {
        try {
            if (data == null) {
                data = this.planet.serialize();
            }
            localStorage.setItem("icoPlanetData", data);
        } catch (err) {
            console.error("Failed to save planet data to localStorage:", err);
        }
    }

    update(state: UpdateState) {
        if(state.keyboardMouse.inputStates.get('control') == true) {
            if (state.keyboardMouse.inputEvents.includes('z-down')) {
                this.undo();
            } else if (state.keyboardMouse.inputEvents.includes('y-down')) {
                this.redo();
            }
        } else {
            this.handleKeyboardMouse(state);
            this.handleTouch(state);
        }
        this.cameraController.setCursor(state);
    }

    handleTouch(state: UpdateState) {
        if (state.touch.inputEvents.includes('touch-up')) {
            this.flattenHeight = undefined;
            this.showCursor();
        }
        if (state.touch.touchCount === 1 && state.touch.position) {
            const cursorPosition = state.touch.position.screenCoordinates.toArray();
            const intersects = this.engine.raycastObjects(cursorPosition, this.planet.terrain.mesh);
            if (state.touch.inputEvents.includes('touch-down') && this.tool && this.tool.isDrawingTool && intersects.length > 0) {
                this.cameraController.setDragControlsEnabled(false);
                this.isDrawing = true;
            }
            if (this.isDrawing && intersects.length > 0) {
                this.showCursor(intersects[0]);
                this.edit(intersects[0], state.deltaTime);
            }
        }

        if (state.touch.inputEvents.includes('touch-up')) {
            this.cameraController.setDragControlsEnabled(true);
            this.isDrawing = false;
            this.showCursor();
        }
    }

    handleKeyboardMouse(state: UpdateState) {
        if (state.keyboardMouse.inputEvents.includes('left-click-up')) {
            this.flattenHeight = undefined;
        }
        const cursorPosition = state.keyboardMouse.position.screenCoordinates.toArray();
        const intersects = this.engine.raycastObjects(cursorPosition, this.planet.terrain.mesh);
        if(state.keyboardMouse.scrollDelta && state.keyboardMouse.inputStates.get('shift') == true) {
            this.setBrushSize(this.brushSizeDisplay - (state.keyboardMouse.scrollDelta / 15.0));
        }

        if (state.keyboardMouse.inputStates.get('left-click')) {
            if (state.keyboardMouse.inputEvents.includes('left-click-down') && this.tool && this.tool.isDrawingTool && intersects.length > 0) {
                this.cameraController.setDragControlsEnabled(false);
                this.isDrawing = true;
            }
            if (this.isDrawing && intersects.length > 0) {
                this.edit(intersects[0], state.deltaTime);
            }
        }
        
        if(intersects.length > 0 && !this.cameraController.dragging) {
            this.showCursor(intersects[0]);
        } else {
            this.showCursor();
        }

        if(state.keyboardMouse.inputEvents.includes('left-click-up')) {
            this.cameraController.setDragControlsEnabled(true);
            this.isDrawing = false;
        }
    }

    showCursor(intersect?: THREE.Intersection) {
        if (intersect && this.tool && [EditorTools.raise, EditorTools.lower, EditorTools.paint, EditorTools.smooth, EditorTools.level, EditorTools.clear, EditorTools.items].includes(this.tool)) {
            this.planet.terrain.setCursor(intersect.point, this._brushSize);
        } else {
            this.planet.terrain.disableCursor();
        }
    }

    edit(intersect: THREE.Intersection, deltaTime: number) {
        const face = intersect.faceIndex!;
        this.showCursor(intersect);
        const localPoint = intersect.point.clone().applyMatrix4(this.planet.scene.matrix.clone().invert());
        let updatePerformed = true;
        if (this.tool === EditorTools.smooth) {
            this.planet.terrain.smoothTerrain(localPoint, face, this._brushSize, deltaTime);
        } else if (this.tool === EditorTools.level) {
            if (!this.flattenHeight) {
                this.flattenHeight = this.planet.terrain.getFaceHeight(face);
            }
            this.planet.terrain.flattenTerrain(localPoint, face, this.flattenHeight, this._brushSize);
        } else if (this.tool === EditorTools.paint) {
            this.planet.terrain.paintTerrain(localPoint, face, this._brushSize, this.color);
        } else if (this.tool === EditorTools.raise) {
            this.planet.terrain.raiseTerrain(localPoint, face, true, this._brushSize, deltaTime);
        } else if (this.tool === EditorTools.lower) {
            this.planet.terrain.raiseTerrain(localPoint, face, false, this._brushSize, deltaTime);
        } else if (this.tool === EditorTools.clear) {
            const locationIds = this.planet.terrain.getLocationsInNormalizedRadius(localPoint, face, this._brushSize);
            this.planet.scenery.removeScenery(locationIds);
        } else if (this.tool === EditorTools.items && this.model) {
            const locationIds = this.planet.terrain.getLocationsInNormalizedRadius(localPoint, face, this._brushSize)
            this.planet.scenery.addScenery(this.model.id, this.color, locationIds);
        } else {
            updatePerformed = false;
        }
        if (updatePerformed) {
            this.onUpdateCallback.call();
        }
    }

    undo() {
        this.history.undo((undoData) => {
            this.planet.deserialize(undoData);
        }, this.planet.serialize());
    }

    redo() {
        this.history.redo((undoData) => {
            this.planet.deserialize(undoData);
        }, this.planet.serialize());
    }

    // Internal brush size is based on dot product from raycast intersection.
    setBrushSize(size: number) {
        if (size == this.brushSizeDisplay) return;
        this.brushSizeDisplay = Math.min(Math.max(0.0025, size), 1.0);
        this._brushSize = 1.0 - (this.brushSizeDisplay / 10.0);
        this.onBrushSizeUpdateCallback.call(this.brushSizeDisplay);
    }

    dispose() {
        this.background.dispose();
        this.planet.dispose();
        this.axes.dispose();
        this.cameraController.dispose();
        super.dispose();
    }
}

export default PlanetEditorScene;
